import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";

import "./course.video.cmp.scss";
import ReactPlayer from "react-player";
import CourseComander from "../../../store/commanders/course.comander";
import courseComander from "../../../store/commanders/course.comander";
import { debounce } from "lodash";
import PLButton from "../../common/button/PLButton";
import { getDisplayTitle } from "../../../helpers/course.helper";
import CourseTranscriptCmp from "../course.transcript/course.transcript.cmp";
import publicityLogo from "../../../assets/logo-coach.png";
import {
  FMOLHS_PUBLICITY_BANNER,
  isPublicityEnabled,
} from "../../../helpers/publicity.banner.helper";
import { useTranslation } from "react-i18next";

const DEFAULT_PUBLICITY_SECONDS = 5;
const IS_VIDEO_PUBLICITY_ENABLED = isPublicityEnabled({
  area: "COURSE",
  kind: "VIDEO",
});

const formatVttTimestamp = (seconds: number) => {
  const safeSeconds = Math.max(0, Number(seconds) || 0);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const remainingSeconds = safeSeconds % 60;

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0",
  )}:${remainingSeconds.toFixed(3).padStart(6, "0")}`;
};

export const buildCaptionTrack = (words: any[]) => {
  if (!Array.isArray(words) || words.length === 0) return "";

  const cues: string[] = [];
  for (let index = 0; index < words.length; index += 10) {
    const group = words.slice(index, index + 10);
    const first = group[0];
    const last = group[group.length - 1];
    const caption = group
      .map((word) => String(word.punct || "").trim())
      .filter(Boolean)
      .join(" ")
      .replace(/\s+([,.;!?])/g, "$1");

    if (!caption) continue;

    cues.push(
      `${cues.length + 1}\n${formatVttTimestamp(first.start_time)} --> ${formatVttTimestamp(
        last.end_time,
      )}\n${caption}`,
    );
  }

  return `WEBVTT\n\n${cues.join("\n\n")}`;
};

const getPublicityConfig = (selected) => {
  if (!IS_VIDEO_PUBLICITY_ENABLED) {
    return null;
  }

  if (!selected || selected.publicity === false) {
    return null;
  }

  const publicity =
    typeof selected.publicity === "string"
      ? { imageUrl: selected.publicity }
      : selected.publicity || selected.preRoll || selected.advertisement || {};

  if (publicity.enabled === false) {
    return null;
  }

  const durationSeconds = Number(
    publicity.durationSeconds ||
      publicity.duration ||
      selected.publicityDurationSeconds ||
      DEFAULT_PUBLICITY_SECONDS
  );

  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    return null;
  }

  const videoUrl =
    publicity.videoUrl || publicity.video || selected.publicityVideoUrl;
  const explicitImageUrl =
    publicity.imageUrl || publicity.image || selected.publicityImageUrl;
  const imageUrl =
    explicitImageUrl ||
    (!videoUrl ? FMOLHS_PUBLICITY_BANNER.imageUrl : undefined);
  const logoUrl =
    publicity.logoUrl ||
    publicity.logo ||
    selected.publicityLogoUrl ||
    (!explicitImageUrl && !videoUrl
      ? FMOLHS_PUBLICITY_BANNER.logoUrl
      : undefined);
  const background =
    publicity.background ||
    publicity.backgroundCss ||
    publicity.backgroundGradient ||
    publicity.gradient ||
    selected.publicityBackground ||
    FMOLHS_PUBLICITY_BANNER.background;
  const theme =
    publicity.theme ||
    selected.publicityTheme ||
    FMOLHS_PUBLICITY_BANNER.theme;

  return {
    durationSeconds: Math.ceil(durationSeconds),
    background,
    imageUrl,
    logoUrl,
    theme,
    videoUrl,
    linkUrl:
      publicity.linkUrl ||
      publicity.href ||
      selected.publicityLinkUrl ||
      FMOLHS_PUBLICITY_BANNER.linkUrl,
    title: publicity.title || FMOLHS_PUBLICITY_BANNER.title,
    subtitle: publicity.subtitle || FMOLHS_PUBLICITY_BANNER.subtitle,
    ctaLabel: publicity.ctaLabel || FMOLHS_PUBLICITY_BANNER.ctaLabel,
  };
};

const CourseVideoCmp = ({ selected, getTranscripts, onTranscriptData }) => {
  const { t } = useTranslation();
  const transcriptUrl = selected?.transcript;
  const isHlsSource =
    typeof selected?.url === "string" && selected.url.includes(".m3u8");
  const selectedAdvertisement = selected?.advertisement;
  const selectedPreRoll = selected?.preRoll;
  const selectedPublicity = selected?.publicity;
  const selectedPublicityDurationSeconds =
    selected?.publicityDurationSeconds;
  const selectedPublicityBackground = selected?.publicityBackground;
  const selectedPublicityImageUrl = selected?.publicityImageUrl;
  const selectedPublicityLinkUrl = selected?.publicityLinkUrl;
  const selectedPublicityLogoUrl = selected?.publicityLogoUrl;
  const selectedPublicityTheme = selected?.publicityTheme;
  const selectedPublicityVideoUrl = selected?.publicityVideoUrl;
  const publicityConfig = useMemo(
    () =>
      getPublicityConfig({
        advertisement: selectedAdvertisement,
        preRoll: selectedPreRoll,
        publicity: selectedPublicity,
        publicityBackground: selectedPublicityBackground,
        publicityDurationSeconds: selectedPublicityDurationSeconds,
        publicityImageUrl: selectedPublicityImageUrl,
        publicityLinkUrl: selectedPublicityLinkUrl,
        publicityLogoUrl: selectedPublicityLogoUrl,
        publicityTheme: selectedPublicityTheme,
        publicityVideoUrl: selectedPublicityVideoUrl,
      }),
    [
      selectedAdvertisement,
      selectedPreRoll,
      selectedPublicity,
      selectedPublicityBackground,
      selectedPublicityDurationSeconds,
      selectedPublicityImageUrl,
      selectedPublicityLinkUrl,
      selectedPublicityLogoUrl,
      selectedPublicityTheme,
      selectedPublicityVideoUrl,
    ]
  );
  const [isMarked, setIsMarked] = useState(selected.finish);
  const [playing, setPlaying] = useState(false);
  const [showPublicity, setShowPublicity] = useState(
    Boolean(publicityConfig)
  );
  const [publicitySecondsLeft, setPublicitySecondsLeft] = useState(
    publicityConfig?.durationSeconds || 0
  );
  const [publicityImageFailed, setPublicityImageFailed] = useState(false);
  const playerRef = useRef<any>(null);
  const transcriptRef = useRef<any>(null);
  const [loadingTranscripts, setLoadingTranscripts] = useState(false);
  const [transcripts, setTranscripts] = useState([]);
  const [captionTrackUrl, setCaptionTrackUrl] = useState("");
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [useNativeHls, setUseNativeHls] = useState(false);
  const [isMobileViewer, setIsMobileViewer] = useState(false);
  const [hasPlaybackStarted, setHasPlaybackStarted] = useState(false);
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const showPublicityLogoLayout = Boolean(
    publicityConfig?.logoUrl && !publicityConfig?.videoUrl
  );
  const publicityStyle = publicityConfig?.background
    ? ({
        "--video-publicity-background": publicityConfig.background,
      } as React.CSSProperties)
    : undefined;
  const publicityThemeClass = publicityConfig?.theme
    ? `video-publicity-shot--${publicityConfig.theme}`
    : "";

  // Use refs for values that don't need to trigger re-renders
  const currentTimeRef = useRef(0);
  const maxWatchedTimeRef = useRef(0);

  // localStorage functions
  const LOCAL_STORAGE_KEY = "course_video_max_watched_times";

  const isMobileViewport = useCallback(() => {
    if (typeof window === "undefined") {
      return false;
    }

    const userAgent = window.navigator.userAgent || "";
    const isTouchDevice =
      window.matchMedia("(pointer: coarse)").matches ||
      window.navigator.maxTouchPoints > 0 ||
      "ontouchstart" in window;
    const isMobileUserAgent =
      /Android|iPhone|iPad|iPod|Mobile|CriOS|FxiOS|EdgiOS/i.test(userAgent);

    return isTouchDevice || isMobileUserAgent;
  }, []);

  const getMediaElement = useCallback(() => {
    if (useNativeHls && playerRef.current instanceof HTMLVideoElement) {
      return playerRef.current;
    }

    if (playerRef.current?.getInternalPlayer) {
      const internalPlayer = playerRef.current.getInternalPlayer();
      if (internalPlayer instanceof HTMLVideoElement) {
        return internalPlayer;
      }
    }

    const videoElement = videoContainerRef.current?.querySelector("video");
    return videoElement instanceof HTMLVideoElement ? videoElement : null;
  }, [useNativeHls]);

  const enterNativeFullscreen = useCallback(async () => {
    if (!isMobileViewer) {
      return;
    }

    const mediaElement = getMediaElement();

    if (!mediaElement) {
      return;
    }

    const fullscreenMediaElement = mediaElement as HTMLVideoElement & {
      webkitEnterFullscreen?: () => void;
      webkitDisplayingFullscreen?: boolean;
    };

    if (
      document.fullscreenElement === mediaElement ||
      fullscreenMediaElement.webkitDisplayingFullscreen
    ) {
      return;
    }

    try {
      if (typeof fullscreenMediaElement.webkitEnterFullscreen === "function") {
        fullscreenMediaElement.webkitEnterFullscreen();
        return;
      }

      if (typeof mediaElement.requestFullscreen === "function") {
        await mediaElement.requestFullscreen();
      }
    } catch (error) {
      console.info("Native fullscreen request was ignored by the browser", error);
    }
  }, [getMediaElement, isMobileViewer]);

  const loadMaxWatchedTimes = () => {
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      return {};
    }
  };

  const saveMaxWatchedTimes = useCallback((videoId: string, time: number) => {
    try {
      const maxWatchedTimes = loadMaxWatchedTimes();
      maxWatchedTimes[videoId] = time;
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(maxWatchedTimes));
    } catch (error) {
      // Silent fail
      console.error("Error saving max watched times:", error);
    }
  }, []);

  // Always resume after seek
  const seekToTime = useCallback((time: number) => {
    if (playerRef.current) {
      if (typeof playerRef.current.seekTo === "function") {
        playerRef.current.seekTo(time, "seconds");
      } else if ("currentTime" in playerRef.current) {
        playerRef.current.currentTime = time;
      }
      setPlaying(true); // ensure playback resumes
    }
  }, []);

  const ensureVisibility = useCallback(() => {
    if (!transcriptRef.current) return;

    const selectedElement = Array.from(transcriptRef.current.childNodes).find(
      (el: any) => el.className === "selected-word"
    ) as HTMLElement;

    if (selectedElement) {
      transcriptRef.current.scrollTop =
        selectedElement.offsetTop - (transcriptRef.current.offsetHeight - 20) >
        0
          ? selectedElement.offsetTop
          : 0;
    }
  }, []);

  const ensureVisibilityDebounced = useMemo(
    () => debounce(ensureVisibility, 300),
    [ensureVisibility]
  );

  useEffect(() => {
    return () => {
      ensureVisibilityDebounced.cancel();
    };
  }, [ensureVisibilityDebounced]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);
    updatePreference();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updatePreference);
      return () => mediaQuery.removeEventListener("change", updatePreference);
    }

    mediaQuery.addListener(updatePreference);
    return () => mediaQuery.removeListener(updatePreference);
  }, []);

  useEffect(() => {
    const captionContent = buildCaptionTrack(transcripts);
    if (!captionContent || typeof URL.createObjectURL !== "function") {
      setCaptionTrackUrl("");
      return;
    }

    const url = URL.createObjectURL(
      new Blob([captionContent], { type: "text/vtt" }),
    );
    setCaptionTrackUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [transcripts]);

  const handlePlaybackProgress = useCallback(
    (currentSeconds: number, playedRatio?: number) => {
      currentTimeRef.current = currentSeconds;
      ensureVisibilityDebounced();

      if (
        currentSeconds > maxWatchedTimeRef.current &&
        currentSeconds - maxWatchedTimeRef.current <= 10 &&
        playing
      ) {
        maxWatchedTimeRef.current = currentSeconds;
        saveMaxWatchedTimes(selected.id, currentSeconds);
      }

      if (playedRatio !== undefined && playedRatio >= 0.8 && !isMarked) {
        CourseComander.cmd_update_lastSeen(selected.id);
        setIsMarked(true);
      }
    },
    [
      ensureVisibilityDebounced,
      isMarked,
      playing,
      saveMaxWatchedTimes,
      selected.id,
    ]
  );

  const onWordClick = useCallback(
    (e, time) => {
      const seekTime = Number(time);
      // Prevent seeking forward beyond max watched time unless video is completed
      if (!isMarked && seekTime > maxWatchedTimeRef.current + 2) {
        seekToTime(maxWatchedTimeRef.current);
      } else {
        seekToTime(seekTime);
      }
    },
    [isMarked, seekToTime]
  );

  const markPlaybackStarted = useCallback(() => {
    setHasPlaybackStarted(true);
    setPlaying(true);
  }, []);

  useEffect(() => {
    setLoadingTranscripts(true);

    // Load max watched times for this video
    const maxWatchedTimes = loadMaxWatchedTimes();
    const storedMaxTime = maxWatchedTimes[selected.id] || 0;
    maxWatchedTimeRef.current = storedMaxTime;

    setIsMarked(selected.finish || false);

    const fetchTranscript = async () => {
      const result = await getTranscripts({ transcript: transcriptUrl });
      setLoadingTranscripts(false);
      setTranscripts(result);
    };

    fetchTranscript();
  }, [getTranscripts, selected.finish, selected.id, selected.url, transcriptUrl]);

  // Reset state when switching videos
  useEffect(() => {
    setPlaying(false);
    currentTimeRef.current = 0;
    setHasPlaybackStarted(false);
  }, [selected.url]);

  useEffect(() => {
    if (!publicityConfig) {
      setShowPublicity(false);
      setPublicitySecondsLeft(0);
      setPublicityImageFailed(false);
      return;
    }

    let remainingSeconds = publicityConfig.durationSeconds;
    setShowPublicity(true);
    setPublicitySecondsLeft(remainingSeconds);
    setPublicityImageFailed(false);
    setPlaying(false);
    setHasPlaybackStarted(false);

    const intervalId = window.setInterval(() => {
      remainingSeconds -= 1;

      if (remainingSeconds <= 0) {
        window.clearInterval(intervalId);
        setShowPublicity(false);
        setPublicitySecondsLeft(0);
        setPlaying(false);
        return;
      }

      setPublicitySecondsLeft(remainingSeconds);
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [publicityConfig, selected.id]);

  useEffect(() => {
    if (!isHlsSource || typeof document === "undefined") {
      setUseNativeHls(false);
      return;
    }

    const testVideo = document.createElement("video");
    const nativeHlsSupport = testVideo.canPlayType(
      "application/vnd.apple.mpegurl"
    );
    const userAgent = window.navigator.userAgent || "";
    const platform = window.navigator.platform || "";
    const maxTouchPoints = window.navigator.maxTouchPoints || 0;
    const isIOSDevice =
      /iPhone|iPad|iPod/i.test(userAgent) ||
      (platform === "MacIntel" && maxTouchPoints > 1);
    const isAndroidDevice = /Android/i.test(userAgent);
    const isMobileDevice = isIOSDevice || isAndroidDevice;

    setUseNativeHls(
      isMobileDevice &&
        (nativeHlsSupport === "probably" || nativeHlsSupport === "maybe")
    );
  }, [isHlsSource, selected.url]);

  useEffect(() => {
    const videoElement = videoContainerRef.current?.querySelector("video");

    if (!(videoElement instanceof HTMLVideoElement)) {
      return;
    }

    videoElement.setAttribute("playsinline", "true");
    videoElement.setAttribute("webkit-playsinline", "true");
    videoElement.setAttribute("x-webkit-airplay", "deny");
    videoElement.disablePictureInPicture = true;

    if ("disableRemotePlayback" in videoElement) {
      videoElement.disableRemotePlayback = true;
    }
  }, [selected.url, useNativeHls]);

  useEffect(() => {
    if (onTranscriptData) {
      onTranscriptData({
        transcripts,
        currentTime: currentTimeRef.current,
        loading: loadingTranscripts,
        onWordClick,
      });
    }
  }, [transcripts, loadingTranscripts, onTranscriptData, onWordClick]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const updateLandscapeMode = () => {
      const isMobile = isMobileViewport();
      setIsMobileViewer(isMobile);
    };

    updateLandscapeMode();

    window.addEventListener("resize", updateLandscapeMode);
    window.addEventListener("orientationchange", updateLandscapeMode);

    return () => {
      window.removeEventListener("resize", updateLandscapeMode);
      window.removeEventListener("orientationchange", updateLandscapeMode);
    };
  }, [isMobileViewport]);

  useEffect(() => {
    if (!isMobileViewer || !playing || !hasPlaybackStarted) {
      return;
    }

    void enterNativeFullscreen();
  }, [enterNativeFullscreen, hasPlaybackStarted, isMobileViewer, playing]);

  return (
    <>
      {/* Course Title Header */}
      <div className="course-video-header">
        <h2 className="course-video-title">{getDisplayTitle(selected)}</h2>
        <PLButton
          onClick={() => courseComander.ac_next_lesson()}
          variant="primary"
          size="lg"
          className="next-lesson-btn"
        >
          {t("common.next")}
        </PLButton>
      </div>

      <div ref={videoContainerRef} className="video-container">
        {showPublicity && publicityConfig ? (
          <div
            className={`video-publicity-shot ${publicityThemeClass}`}
            style={publicityStyle}
          >
            {publicityConfig.videoUrl ? (
              <video
                src={publicityConfig.videoUrl}
                autoPlay={!prefersReducedMotion}
                muted
                loop
                playsInline
                preload="auto"
                aria-hidden="true"
                tabIndex={-1}
              />
            ) : showPublicityLogoLayout ? (
              <div className="video-publicity-shot__logo-layout">
                {publicityConfig.imageUrl && !publicityImageFailed ? (
                  <img
                    className="video-publicity-shot__background-image"
                    src={publicityConfig.imageUrl}
                    alt=""
                    aria-hidden="true"
                    onError={() => setPublicityImageFailed(true)}
                  />
                ) : null}
                <div className="video-publicity-shot__logo-panel">
                  <img
                    className="video-publicity-shot__logo"
                    src={publicityConfig.logoUrl}
                    alt={publicityConfig.title}
                  />
                </div>
              </div>
            ) : publicityConfig.imageUrl && !publicityImageFailed ? (
              <img
                src={publicityConfig.imageUrl}
                alt={publicityConfig.title}
                onError={() => setPublicityImageFailed(true)}
              />
            ) : (
              <div className="video-publicity-shot__fallback">
                <img src={publicityLogo} alt={publicityConfig.title} />
              </div>
            )}

            {!showPublicityLogoLayout ? (
              <div className="video-publicity-shot__scrim" />
            ) : null}
            {showPublicityLogoLayout && publicityConfig.linkUrl ? (
              <a
                className="video-publicity-shot__click-layer"
                href={publicityConfig.linkUrl}
                target="_blank"
                rel="noreferrer"
                aria-label={publicityConfig.title}
              />
            ) : null}
            <div
              className={`video-publicity-shot__content ${
                showPublicityLogoLayout
                  ? "video-publicity-shot__content--logo-layout"
                  : ""
              }`}
            >
              <div className="video-publicity-shot__brand">
                {publicityConfig.title}
              </div>
              <div className="video-publicity-shot__subtitle">
                {publicityConfig.subtitle}
              </div>
              {publicityConfig.linkUrl ? (
                <a
                  className="video-publicity-shot__link"
                  href={publicityConfig.linkUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {publicityConfig.ctaLabel}
                </a>
              ) : null}
            </div>
            <div className="video-publicity-shot__countdown" aria-live="polite">
              {t("course.lessonStartsIn", { count: publicitySecondsLeft })}
            </div>
          </div>
        ) : isHlsSource && useNativeHls ? (
          <video
            ref={playerRef}
            src={selected.url}
            controls
            playsInline
            autoPlay={playing}
            preload="metadata"
            controlsList="nodownload"
            disablePictureInPicture
            style={{ width: "100%", height: "auto" }}
            onLoadedMetadata={() => setPlaying(false)}
            onPlay={() => {
              markPlaybackStarted();
            }}
            onPause={() => setPlaying(false)}
            onTimeUpdate={(event) => {
              const videoElement = event.currentTarget;
              const currentSeconds = videoElement.currentTime || 0;
              const duration = videoElement.duration || 0;
              const playedRatio = duration > 0 ? currentSeconds / duration : 0;

              handlePlaybackProgress(currentSeconds, playedRatio);
            }}
            onError={(event) => {
              const mediaError = event.currentTarget.error;

              console.error("Native HLS video error:", {
                code: mediaError?.code,
                message: mediaError?.message,
                currentSrc: event.currentTarget.currentSrc,
                networkState: event.currentTarget.networkState,
                readyState: event.currentTarget.readyState,
              });
              setPlaying(false);
            }}
          >
            {captionTrackUrl ? (
              <track
                kind="captions"
                src={captionTrackUrl}
                srcLang="en"
                label={t("accessibility.englishCaptions")}
                default
              />
            ) : null}
          </video>
        ) : (
          // @ts-ignore
          <ReactPlayer
            url={selected.url}
            controls={true}
            playing={playing}
            playsinline={true}
            width="100%"
            height="auto"
            config={{
              file: {
                forceHLS: isHlsSource && !useNativeHls,
                hlsOptions: {
                  enableWorker: false,
                  lowLatencyMode: false,
                },
                attributes: {
                  controlsList: "nodownload",
                  playsInline: true,
                  "webkit-playsinline": "true",
                },
                tracks: captionTrackUrl
                  ? [
                      {
                        kind: "captions",
                        src: captionTrackUrl,
                        srcLang: "en",
                        label: t("accessibility.englishCaptions"),
                        default: true,
                      },
                    ]
                  : [],
              },
            }}
            onReady={() => setPlaying(false)}
            onError={(...args: any[]) => {
              const [eventOrError, details] = args;

              if (eventOrError === "hlsError") {
                const errorSummary = {
                  type: details?.type,
                  details: details?.details,
                  fatal: details?.fatal,
                  reason: details?.reason,
                  response: details?.response
                    ? {
                        code: details.response.code,
                        text: details.response.text,
                      }
                    : undefined,
                  networkDetails: details?.networkDetails
                    ? {
                        status: details.networkDetails.status,
                        statusText: details.networkDetails.statusText,
                        responseURL:
                          details.networkDetails.responseURL ||
                          details.networkDetails.responseUrl,
                      }
                    : undefined,
                  error:
                    details?.error instanceof Error
                      ? {
                          name: details.error.name,
                          message: details.error.message,
                        }
                      : details?.error,
                  frag: details?.frag
                    ? {
                        sn: details.frag.sn,
                        level: details.frag.level,
                        relurl: details.frag.relurl,
                        baseurl: details.frag.baseurl,
                      }
                    : undefined,
                };

                console.error("ReactPlayer HLS error:", {
                  event: eventOrError,
                  details: errorSummary,
                });

                if (details?.fatal) {
                  setPlaying(false);
                }

                return;
              }

              console.error("ReactPlayer error:", ...args);
              setPlaying(false);
            }}
            onProgress={(e: any) => {
              handlePlaybackProgress(e.playedSeconds, e.played);
            }}
            onPlay={() => {
              markPlaybackStarted();
            }}
            progressInterval={1000}
            ref={playerRef}
          />
        )}
      </div>

      {/* Transcript - hidden on mobile via CSS */}
      <CourseTranscriptCmp
        transcripts={transcripts}
        currentTime={currentTimeRef.current}
        loading={loadingTranscripts}
        onWordClick={onWordClick}
      />
    </>
  );
};

export default CourseVideoCmp;
